import React, { useCallback, useState } from "react";
import { Message } from "components/Atoms/Message";
import Panel from "components/panels/panel";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { GuestList } from "./GuestList";
import { ButtonSecondary } from "components/Atoms/Button";
import moment from "moment";
import { useOpenNewGuestSidePanel } from "components/SidePanel/Guestlist/NewGuestSidePanel";
import { useOpenGuestlistConfigSidePanel } from "components/SidePanel/Guestlist/GuestlistConfigSidePanel";
import { PhosphorDownloadSimpleButton, PhosphorGearButton } from "components/Atoms/PhosphorIconButton";
import { useCustomQuery } from "utilities/hookutils";
import { getRealStartEndDate } from "utilities/showDateUtils";
import { ShowDates } from "components/showdatepicker/models";
import { useOpenNewAccessTypeSidePanel } from "components/SidePanel/Guestlist/NewAccessTypeSidePanel";

interface Props {
  showId: number
  showDates: ShowDates
  canActivate: boolean
  panelConfig: server.dto.GuestlistPanelConfig
}

const GuestlistPanel = ({ showId, showDates, canActivate, panelConfig }: Props) => {
  const openNewGuestSidePanel = useOpenNewGuestSidePanel();
  const openGuestlistConfigSidePanel = useOpenGuestlistConfigSidePanel();
  const openNewAccessTypeSidePanel = useOpenNewAccessTypeSidePanel();

  const [guestlistId, setGuestlistId] = useState<number | null>(panelConfig.guestlistId || null);
  const hasGuestlist = guestlistId != null;
  const [isActivating, setIsActivating] = useState(false);

  const { startRealDate } = getRealStartEndDate(showDates);

  const guestlistConfigApi = useCustomQuery({
    queryFn: () => window.CoreApi.Guestlist.GetSidePanelData(guestlistId).then(res => res.data),
    queryKey: ["guestlistConfiguration", guestlistId],
    enabled: hasGuestlist,
  });

  const guestlistConfigData = guestlistConfigApi.data?.data;

  const onAddClickedFn = useCallback(() => {
    if (hasGuestlist && guestlistConfigData?.permissions.canAddGuest) {
      return () => openNewGuestSidePanel(guestlistId, () => { });
    }

    return () => { };
  }, [hasGuestlist, guestlistConfigData, openNewGuestSidePanel, guestlistId]);

  const onDelete = () => {
    setGuestlistId(null);
  }

  const { refToScrollTo } = useShortcutPanel({
    label: panelConfig.headerLabel,
    totalItems: 0,
    onAddClicked: onAddClickedFn(),
    type: "guestlist"
  });

  const activateGuestlist = async () => {
    setIsActivating(true);
    const resp = await window.CoreApi.Guestlist.CreateGuestlistConfiguration({
      showId: showId,
      allowMultipleGuestsPerName: false,
      deadlineUtc: moment.utc(startRealDate).toDate(),
      guestEmailAddressFormBehavior: "notVisible",
      guestMobileNumberFormBehavior: "notVisible",
      guestNotesFormBehavior: "visibleOptional",
      guestPassportNumberFormBehavior: "notVisible",
    }, { toastOnError: true });
    setIsActivating(false);
    if (!resp.data.hasErrors) {
      setGuestlistId(resp.data.data);
      openGuestlistConfigSidePanel(resp.data.data, onDelete);
    }
  }

  const canAddGuest = guestlistConfigData && guestlistConfigData.lists.length > 0 && guestlistConfigData.permissions.canAddGuest;

  const getHeaderButtons = () => {
    if (!hasGuestlist && !canActivate) {
      return null;
    }

    if (!hasGuestlist) {
      return <ButtonSecondary
        onClick={activateGuestlist}
        label={RESX.Buttons.btnActivate}
        loading={isActivating}
      />;
    }

    return <>
      {canAddGuest && <PhosphorDownloadSimpleButton
        onClick={() => window.location.href = window.CoreApi.Guestlist.ExportXslx.absoluteUrl(guestlistId)}
        layout="in-square"
        backgroundColor="transparent"
        padding="regular"
      />}
      {guestlistConfigData?.permissions.canConfigure && <PhosphorGearButton
        onClick={() => openGuestlistConfigSidePanel(guestlistId, onDelete)}
        layout="in-square"
        backgroundColor="transparent"
        padding="regular"
      />}
      {canAddGuest && <ButtonSecondary
        onClick={() => openNewGuestSidePanel(guestlistId, () => { })}
        label={RESX.Guestlist.AddGuestBtn}
      />}
    </>
  }

  return <>
    <Panel
      panelClassName="s1_related-feature-panel"
      contentCssClass="flex-column gap-large"
      panelRef={refToScrollTo}
      title={panelConfig.headerLabel}
      button={getHeaderButtons()}
      hasData={guestlistConfigData != undefined}
    >
      {(guestlistConfigData && guestlistConfigData.permissions.canConfigure && guestlistConfigData.lists.length === 0) &&
        <div className="flex gap">
          <Message className="grow1" type="accent">{RESX.GuestlistErrors.AddAtLeast1List}</Message>
          <div>
            <ButtonSecondary onClick={() => openNewAccessTypeSidePanel(guestlistId, () => {
              guestlistConfigApi.refetch();
            })} label={RESX.Buttons.Add} />
          </div>
        </div>}
      {(guestlistConfigData && !guestlistConfigData.permissions.canConfigure && guestlistConfigData.lists.length === 0) &&
        <Message type="accent">{RESX.GuestlistErrors.YouCannotAddGuestsUntilAdminCompletedTheSetup}</Message>}
      {(guestlistConfigData && guestlistConfigData.lists.length > 0) &&
        <GuestList guestlistId={guestlistId} lists={guestlistConfigData.lists} />}
    </Panel>
  </>
}

export default GuestlistPanel;